<script setup lang="ts">
    type LightningGlobal = {
        use: (app: string, callback: () => void, endpoint: string) => void;
        createComponent: (
            componentName: string,
            componentProps: Record<string, unknown>,
            targetId: string,
            callback: () => void
        ) => void;
    };

    const {
        scriptKey,
        scriptSrc,
        endpoint,
        appName,
        componentName,
        componentProps,
        targetId = 'lightning-out',
        timeoutMs = 15_000
    } = defineProps<{
        scriptKey: string;
        scriptSrc: string;
        endpoint: string;
        appName: string;
        componentName: string;
        componentProps: Record<string, unknown>;
        targetId?: string;
        timeoutMs?: number;
    }>();

    const showLoadingSpinner = ref(true);
    const showError = ref(false);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasInitialized = false;

    const stopLoadingWithError = () => {
        showLoadingSpinner.value = false;
        showError.value = true;
    };

    const stopLoadingWithSuccess = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }

        showLoadingSpinner.value = false;
        showError.value = false;
    };

    const mountLightningOut = (): boolean => {
        if (hasInitialized) return true;

        const lightning = (window as typeof window & { $Lightning?: LightningGlobal }).$Lightning;
        if (!lightning) return false;

        hasInitialized = true;

        try {
            lightning.use(
                appName,
                () => {
                    try {
                        lightning.createComponent(componentName, componentProps, targetId, () => {
                            stopLoadingWithSuccess();
                        });
                    } catch {
                        stopLoadingWithError();
                    }
                },
                endpoint
            );

            return true;
        } catch {
            stopLoadingWithError();
            return false;
        }
    };

    onMounted(() => {
        timeoutId = setTimeout(() => {
            stopLoadingWithError();
        }, timeoutMs);

        mountLightningOut();
    });

    onBeforeUnmount(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    });

    useHead({
        script: [
            {
                key: scriptKey,
                src: scriptSrc,
                defer: true,
                onload: () => {
                    mountLightningOut();
                },
                onerror: () => {
                    stopLoadingWithError();
                }
            }
        ]
    });
</script>

<template>
    <div :id="targetId">
        <slot
            v-if="showLoadingSpinner"
            name="loading"
        >
            <LoadingSpinner />
        </slot>

        <slot
            v-else-if="showError"
            name="error"
        />
    </div>
</template>
